import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupGridThumbnailNav();
  }

  #controller = new AbortController();
  /** @type {IntersectionObserver | null} */
  #gridObserver = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#gridObserver?.disconnect();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  #setupGridThumbnailNav() {
    if (this.dataset.presentation !== 'grid') return;

    const thumbNav = this.querySelector('.media-gallery__thumb-nav');
    if (!thumbNav) return;

    const thumbBtns = /** @type {NodeListOf<HTMLElement>} */ (thumbNav.querySelectorAll('.media-gallery__thumb-btn'));
    const gridItems = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.media-gallery__grid > li'));

    if (!thumbBtns.length || !gridItems.length) return;

    const { signal } = this.#controller;

    const setActive = (index) => {
      thumbBtns.forEach((btn, i) => btn.classList.toggle('is-active', i === index));
      thumbBtns[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    thumbNav.addEventListener(
      'click',
      (event) => {
        const btn = /** @type {HTMLElement | null} */ (
          /** @type {HTMLElement} */ (event.target).closest('.media-gallery__thumb-btn')
        );
        if (!btn) return;
        const index = parseInt(btn.dataset.thumbIndex ?? '0', 10);
        gridItems[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
      { signal }
    );

    /** @type {Set<Element>} */
    const visible = new Set();

    this.#gridObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visible.add(entry.target);
          } else {
            visible.delete(entry.target);
          }
        }

        if (!visible.size) return;

        let topmost = null;
        let topmostY = Infinity;
        for (const el of visible) {
          const y = el.getBoundingClientRect().top;
          if (y < topmostY) {
            topmostY = y;
            topmost = el;
          }
        }

        if (!topmost) return;
        const index = Array.from(gridItems).indexOf(/** @type {HTMLElement} */ (topmost));
        if (index !== -1) setActive(index);
      },
      { threshold: 0.1 }
    );

    for (const item of gridItems) {
      this.#gridObserver.observe(item);
    }
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
